import {
  collection,
  addDoc,
  query,
  orderBy,
  limit,
  getDocs,
  getCountFromServer,
  Timestamp,
  where,
} from 'firebase/firestore';
import { db } from '../config/firebase.ts';
import { GameResult, PortfolioGameResult, GridType } from '../types';
import { getUtcDayKey } from '../simulation/dailySeed.ts';
import { sortContainerLeaderboard } from '../utils/containerLeaderboard.ts';
import { sortSurvivorLeaderboard } from '../utils/survivorLeaderboard.ts';

function parseGrid(finalGrid: string | GridType): GridType {
  return typeof finalGrid === 'string' ? (JSON.parse(finalGrid) as GridType) : finalGrid;
}

function mapSurvivalDoc(doc: { id: string; data: () => Record<string, unknown> }): GameResult {
  const data = doc.data();
  return {
    id: doc.id,
    survivalPercentage: data.survivalPercentage as number,
    duration: Math.round(data.duration as number),
    gameType: data.gameType as GameResult['gameType'],
    preBurns: data.preBurns as number,
    config: data.config as GameResult['config'],
    timestamp: (data.timestamp as Timestamp).toMillis(),
    finalGrid: parseGrid(data.finalGrid as string),
    result: data.result as GameResult['result'],
    startingFires: data.startingFires as number,
    mode: 'survival',
    userId: data.userId as string | undefined,
    dayKey: data.dayKey as string | undefined,
    seed: data.seed as number | undefined,
  };
}

function mapPortfolioDoc(doc: { id: string; data: () => Record<string, unknown> }): PortfolioGameResult {
  const data = doc.data();
  return {
    id: doc.id,
    mode: 'portfolio',
    selectedTiles: data.selectedTiles as number[],
    selectedTileCount: data.selectedTileCount as number,
    survivedTileCount: data.survivedTileCount as number,
    won: data.won as boolean,
    duration: Math.round(data.duration as number),
    timestamp: (data.timestamp as Timestamp).toMillis(),
    config: data.config as PortfolioGameResult['config'],
    finalGrid: parseGrid(data.finalGrid as string),
    userId: data.userId as string | undefined,
    dayKey: data.dayKey as string | undefined,
    seed: data.seed as number | undefined,
    instantLoss: data.instantLoss as boolean | undefined,
  };
}

export const gameService = {
  async saveSurvivalResult(result: GameResult): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, 'gameResults'), {
        ...result,
        mode: 'survival',
        dayKey: result.dayKey ?? getUtcDayKey(),
        timestamp: Timestamp.fromMillis(result.timestamp),
        duration: Math.round(result.duration),
        finalGrid: JSON.stringify(result.finalGrid),
      });
      return docRef.id;
    } catch (error) {
      console.error('Error saving survival result:', error);
      throw error;
    }
  },

  async savePortfolioResult(result: PortfolioGameResult): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, 'portfolioResults'), {
        ...result,
        dayKey: result.dayKey ?? getUtcDayKey(),
        timestamp: Timestamp.fromMillis(result.timestamp),
        duration: Math.round(result.duration),
        finalGrid: JSON.stringify(result.finalGrid),
      });
      return docRef.id;
    } catch (error) {
      console.error('Error saving portfolio result:', error);
      throw error;
    }
  },

  /** @deprecated use saveSurvivalResult */
  async saveGameResult(result: GameResult): Promise<void> {
    return this.saveSurvivalResult(result);
  },

  async getLastSurvivalGame(): Promise<GameResult | null> {
    try {
      const q = query(collection(db, 'gameResults'), orderBy('timestamp', 'desc'), limit(1));
      const snapshot = await getDocs(q);
      if (snapshot.empty) return null;
      return mapSurvivalDoc(snapshot.docs[0]);
    } catch (error) {
      console.error('Error fetching last survival game:', error);
      return null;
    }
  },

  async getLastPlayedGame(): Promise<GameResult | null> {
    return this.getLastSurvivalGame();
  },

  async getSurvivalLeaderboard(limitCount = 100, dayKey: string = getUtcDayKey()): Promise<GameResult[]> {
    const q = query(
      collection(db, 'gameResults'),
      where('dayKey', '==', dayKey),
      orderBy('survivalPercentage', 'desc'),
      orderBy('preBurns', 'asc'),
      orderBy('duration', 'asc'),
      limit(limitCount)
    );

    const snapshot = await getDocs(q);
    return sortContainerLeaderboard(snapshot.docs.map(mapSurvivalDoc));
  },

  async getPortfolioLeaderboard(
    limitCount = 100,
    dayKey: string = getUtcDayKey()
  ): Promise<PortfolioGameResult[]> {
    const q = query(
      collection(db, 'portfolioResults'),
      where('dayKey', '==', dayKey),
      where('won', '==', true),
      orderBy('selectedTileCount', 'desc'),
      limit(limitCount)
    );

    const snapshot = await getDocs(q);
    return sortSurvivorLeaderboard(snapshot.docs.map(mapPortfolioDoc));
  },

  async getLeaderboard(_filters = {}, limitCount = 10): Promise<GameResult[]> {
    return this.getSurvivalLeaderboard(limitCount);
  },

  async getSurvivalGamesCount(dayKey: string = getUtcDayKey()): Promise<number> {
    const q = query(collection(db, 'gameResults'), where('dayKey', '==', dayKey));
    const snapshot = await getCountFromServer(q);
    return snapshot.data().count;
  },

  async getPortfolioGamesCount(dayKey: string = getUtcDayKey()): Promise<number> {
    const q = query(collection(db, 'portfolioResults'), where('dayKey', '==', dayKey));
    const snapshot = await getCountFromServer(q);
    return snapshot.data().count;
  },

  async getGamesCount(): Promise<number> {
    return this.getSurvivalGamesCount();
  },

  async getPortfolioWinsCount(dayKey: string = getUtcDayKey()): Promise<number> {
    const q = query(
      collection(db, 'portfolioResults'),
      where('dayKey', '==', dayKey),
      where('won', '==', true)
    );
    const snapshot = await getCountFromServer(q);
    return snapshot.data().count;
  },

  async getStats() {
    try {
      const dayKey = getUtcDayKey();
      const q = query(collection(db, 'gameResults'), where('dayKey', '==', dayKey));
      const snapshot = await getDocs(q);
      const results = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          ...data,
          timestamp: data.timestamp.toMillis(),
          finalGrid: parseGrid(data.finalGrid),
        } as GameResult;
      });

      return {
        totalGames: results.length,
        averageSurvival:
          results.length > 0
            ? results.reduce((acc, game) => acc + game.survivalPercentage, 0) / results.length
            : 0,
        turnBased: results.filter((game) => game.gameType === 'Turn Based').length,
        continuous: results.filter((game) => game.gameType === 'Continuous').length,
      };
    } catch (error) {
      console.error('Error fetching stats:', error);
      return null;
    }
  },
};
